// src/pages/PressRelease.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Chip,
  Divider,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  useMediaQuery,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Link,
  Breadcrumbs,
  CircularProgress,
} from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie } from "../../../../Utils/LangCookie";

// ✅ REAL DATA (WP REST wrapper)
import { apiGetNewsGrouped } from "../../../../api/News";

/* ----------------------------- Hero helpers ------------------------------ */

const fonts = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed JP",
};

const FlagImage = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: 0,
  top: theme.spacing(4),
  width: "48%",
  maxWidth: 560,
  aspectRatio: "21/9",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  maskImage:
    "radial-gradient(120% 120% at 30% 40%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)",
  [theme.breakpoints.down("md")]: {
    width: "70%",
    top: theme.spacing(2),
    opacity: 0.9,
  },
}));

/* ----------------------------- i18n / labels ----------------------------- */

const I18N = {
  breadcrumbHome: { ZH: "首頁", EN: "Home", JP: "ホーム" },
  breadcrumbInfo: { ZH: "最新資訊", EN: "Information", JP: "最新情報" },
  breadcrumbLatest: { ZH: "最新消息", EN: "Latest News", JP: "最新ニュース" },
  pastNewsTitle: { ZH: "過往新聞", EN: "Past News", JP: "過去のニュース" },
  yearLabel: { ZH: "年份", EN: "Year", JP: "年" },
  yearAll: { ZH: "全部", EN: "All", JP: "すべて" },
  filterAll: { ZH: "全部", EN: "All", JP: "すべて" },
  noResult: {
    ZH: "目前無符合條件的新聞。",
    EN: "There are currently no news items matching the filter.",
    JP: "現在、条件に一致するニュースはありません。",
  },
};

/* ----------------------------- Link helpers ----------------------------- */

const safeSeg = (s) => encodeURIComponent(String(s || "").trim());
const buildNewsHref = (newsType, slug) =>
  `/最新資訊/${safeSeg(newsType)}/${safeSeg(slug)}`;

/* ---------------- date helpers (remove weekday) ---------------- */

function parseDateOnlyToDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatPostDate(value, lang) {
  const d = parseDateOnlyToDate(value);
  if (!d) return value ? String(value) : "";

  if (lang === "EN") {
    const parts = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).formatToParts(d);

    const month = parts.find((p) => p.type === "month")?.value || "";
    const day = parts.find((p) => p.type === "day")?.value || "";
    return [month, day].filter(Boolean).join(" ");
  }

  if (lang === "JP") {
    const parts = new Intl.DateTimeFormat("ja-JP", {
      month: "numeric",
      day: "numeric",
    }).formatToParts(d);

    const month = parts.find((p) => p.type === "month")?.value || "";
    const day = parts.find((p) => p.type === "day")?.value || "";
    return `${month}月${day}日`;
  }

  // ZH
  const parts = new Intl.DateTimeFormat("zh-TW", {
    month: "numeric",
    day: "numeric",
  }).formatToParts(d);

  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  return `${month}月${day}日`.trim();
}

function yearFromValue(value) {
  const d = parseDateOnlyToDate(value);
  return d ? d.getFullYear() : null;
}

function monthKeyFromValue(value) {
  const d = parseDateOnlyToDate(value);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function monthTitle(isoMonthKey, lang = "EN") {
  const [y, m] = String(isoMonthKey).split("-");
  const monthIndex = parseInt(m, 10) - 1;
  const MONTHS_EN = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (lang === "EN") return `${MONTHS_EN[monthIndex]} ${y}`;
  if (lang === "JP") return `${y}年${parseInt(m, 10)}月`;
  return `${y}年${parseInt(m, 10)}月`;
}

/* ---------------- thumbnail helpers (WP ONLY) ----------------- */

const WP_GRAPHQL = String(process.env.REACT_APP_WP_GRAPHQL_URL || "").trim();
const WP_ORIGIN_ENV = String(process.env.REACT_APP_WP_ORIGIN || "")
  .trim()
  .replace(/\/+$/, "");

function deriveWpBaseFromGraphqlUrl(graphqlUrl) {
  const raw = String(graphqlUrl || "").trim();
  if (!raw) return "";
  try {
    const u = new URL(raw);
    const path = u.pathname.replace(/\/graphql\/?$/i, "");
    return `${u.origin}${path}`.replace(/\/+$/, "");
  } catch {
    return raw.replace(/\/graphql\/?$/i, "").replace(/\/+$/, "");
  }
}

const WP_BASE =
  WP_ORIGIN_ENV ||
  deriveWpBaseFromGraphqlUrl(WP_GRAPHQL) ||
  "http://localhost/wp-headless";

function safeEncodeUrl(full) {
  try {
    const u = new URL(full);
    u.pathname = u.pathname
      .split("/")
      .map((seg) => encodeURIComponent(decodeURIComponent(seg)))
      .join("/");
    return u.toString();
  } catch {
    return String(full || "").replace(/ /g, "%20");
  }
}

function getWpParts() {
  const wpBaseNorm = String(WP_BASE || "").replace(/\/+$/, "");
  try {
    const u = new URL(wpBaseNorm);
    return {
      wpBaseNorm, // includes /wp-headless
      originOnly: u.origin, // http://localhost
      wpPathBase: u.pathname.replace(/\/+$/, ""), // /wp-headless
    };
  } catch {
    return { wpBaseNorm, originOnly: "", wpPathBase: "" };
  }
}

/**
 * Normalize WP media paths returned from DB/meta:
 * - /uploads/... -> /wp-content/uploads/...
 * - /wp-headless/uploads/... -> /wp-headless/wp-content/uploads/...
 */
function resolveWpAssetUrl(url) {
  if (!url) return "";
  const raw0 = String(url).trim();
  if (!raw0) return "";

  const { wpBaseNorm, originOnly, wpPathBase } = getWpParts();

  const normalizeUploadsPath = (pathname) => {
    if (pathname.startsWith("/uploads/")) {
      return `/wp-content${pathname}`;
    }
    if (wpPathBase && pathname.startsWith(`${wpPathBase}/uploads/`)) {
      return pathname.replace(
        `${wpPathBase}/uploads/`,
        `${wpPathBase}/wp-content/uploads/`
      );
    }
    return pathname;
  };

  // absolute
  if (/^https?:\/\//i.test(raw0)) {
    try {
      const u = new URL(raw0);
      const p = normalizeUploadsPath(u.pathname || "");

      // If it's localhost (any port), force under WP (prevents :5001 issues)
      if (/^(localhost|127\.0\.0\.1)$/i.test(u.hostname)) {
        // path already contains /wp-headless => use originOnly
        if (wpPathBase && p.startsWith(`${wpPathBase}/`)) {
          return safeEncodeUrl(`${originOnly}${p}${u.search || ""}`);
        }
        return safeEncodeUrl(`${wpBaseNorm}${p}${u.search || ""}`);
      }

      // external absolute => keep
      return safeEncodeUrl(raw0);
    } catch {
      return safeEncodeUrl(raw0);
    }
  }

  // relative
  const raw = raw0.startsWith("/") ? raw0 : `/${raw0}`;

  // /uploads/...
  if (raw.startsWith("/uploads/")) {
    return safeEncodeUrl(`${wpBaseNorm}/wp-content${raw}`);
  }

  // /wp-headless/uploads/...
  if (wpPathBase && raw.startsWith(`${wpPathBase}/uploads/`)) {
    const normalized = raw.replace(
      `${wpPathBase}/uploads/`,
      `${wpPathBase}/wp-content/uploads/`
    );
    return safeEncodeUrl(`${originOnly}${normalized}`);
  }

  // /wp-headless/wp-content/...
  if (wpPathBase && raw.startsWith(`${wpPathBase}/wp-content/`)) {
    return safeEncodeUrl(`${originOnly}${raw}`);
  }

  // /wp-content/...
  if (raw.startsWith("/wp-content/")) {
    return safeEncodeUrl(`${wpBaseNorm}${raw}`);
  }

  // fallback as WP relative
  return safeEncodeUrl(`${wpBaseNorm}${raw}`);
}

function pickThumbUrl(n) {
  const raw = n?.thumbnail?.url || n?.thumbnail?.asset?.url || "";
  return resolveWpAssetUrl(raw);
}

/**
 * Supports:
 * - ["商品"] (grouped endpoint)
 * - [{ name: "商品" }] (other endpoints)
 */
function tagNameFromTag(t) {
  if (!t) return "";
  if (typeof t === "string") return t.trim();
  if (typeof t === "object") {
    return String(t?.name ?? t?.label ?? t?.tagName ?? t?.title ?? "").trim();
  }
  return "";
}

function firstTagName(n) {
  const tags = Array.isArray(n?.tags) ? n.tags : [];
  return tagNameFromTag(tags[0]);
}

/* ---------------------------- Year sidebar item --------------------------- */

function YearItem({ mode, label, active, onClick, lang }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      sx={{
        px: 1,
        py: 0.75,
        cursor: "pointer",
        color: active ? getPrimaryColor(mode) : "text.secondary",
        fontWeight: active ? 900 : 700,
        fontFamily: fonts[lang],
        borderLeft: active
          ? `3px solid ${getPrimaryColor(mode)}`
          : "3px solid transparent",
        "&:hover": {
          color: getPrimaryColor(mode),
          backgroundColor: alpha(getPrimaryColor(mode), 0.06),
        },
      }}
    >
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

/* ---------------------------- Component ---------------------------------- */

export default function PressRelease({ mode = "b2b", heroRightImage = "" }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const isTabletPortrait = useMediaQuery(
    "(min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)"
  );

  const isDesktopLayout = isMdUp || isTabletPortrait;

  // --- language awareness ---
  const [lang, setLang] = useState(() => getLangFromCookie() || "EN");
  useEffect(() => {
    const handleLangChange = () => setLang(getLangFromCookie() || "EN");
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  // --- data ---
  const [loading, setLoading] = useState(true);
  const [latestList, setLatestList] = useState([]);

  // ✅ WP DB: re-fetch when lang changes (backend localizes by lang)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        const res = await apiGetNewsGrouped({
          newsType: "最新消息",
          limitPerType: 999,
        });

        if (!alive) return;

        const list = res?.grouped?.["最新消息"] || [];
        setLatestList(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("apiGetNewsGrouped(newsType=最新消息) failed:", e);
        if (!alive) return;
        setLatestList([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [lang]);

  // ✅ dynamic filter keys from tags in response (no duplicates)
  const filterKeys = useMemo(() => {
    const set = new Set();
    (latestList || []).forEach((n) => {
      const tags = Array.isArray(n?.tags) ? n.tags : [];
      tags.forEach((t) => {
        const name = tagNameFromTag(t);
        if (name) set.add(name);
      });
    });

    return ["全部", ...Array.from(set)];
  }, [latestList]);

  // normalize backend rows → posts for this page
  const posts = useMemo(() => {
    const src = Array.isArray(latestList) ? latestList : [];

    return src
      .map((n) => {
        const postDate = n?.postDate || n?.startDate || n?.endDate || null;
        const y = yearFromValue(postDate);
        const mKey = monthKeyFromValue(postDate);

        if (!y || !mKey) return null;

        const category = firstTagName(n); // "" if no tags

        return {
          id: n?.id ?? `${n.newsType}-${n.slug}`,
          isoDateValue: postDate,
          monthKey: mKey,
          year: y,
          displayDate: formatPostDate(postDate, lang),
          title: n?.title || "",
          summary: "",
          category,
          image: pickThumbUrl(n) || "/img/news/pressReleaseTop.png",
          link: buildNewsHref(n?.newsType || "最新消息", n?.slug || ""),
        };
      })
      .filter(Boolean);
  }, [latestList, lang]);

  const [activeCat, setActiveCat] = useState("全部");
  const [activeYear, setActiveYear] = useState("全部");

  // ✅ keep activeCat valid after filterKeys changes
  useEffect(() => {
    if (!filterKeys.includes(activeCat)) setActiveCat("全部");
  }, [filterKeys, activeCat]);

  const years = useMemo(() => {
    const ys = Array.from(new Set(posts.map((p) => p.year))).sort(
      (a, b) => b - a
    );
    return ys;
  }, [posts]);

  const filtered = useMemo(() => {
    return posts
      .filter((p) => (activeCat === "全部" ? true : p.category === activeCat))
      .filter((p) => (activeYear === "全部" ? true : p.year === activeYear));
  }, [posts, activeCat, activeYear]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((p) => {
      const key = p.monthKey;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });

    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([k, items]) => [
        k,
        items.sort((a, b) =>
          String(a.isoDateValue) < String(b.isoDateValue) ? 1 : -1
        ),
      ]);
  }, [filtered]);

  const YearSidebar = (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontFamily: fonts[lang],
          color: "text.secondary",
          mb: isDesktopLayout ? 2 : 1,
        }}
      >
        {I18N.pastNewsTitle[lang]}
      </Typography>

      {!isDesktopLayout && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="year-label">{I18N.yearLabel[lang]}</InputLabel>
          <Select
            labelId="year-label"
            label={I18N.yearLabel[lang]}
            value={activeYear}
            onChange={(e) => setActiveYear(e.target.value)}
          >
            <MenuItem value="全部">{I18N.yearAll[lang]}</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isDesktopLayout && (
        <Stack spacing={0.75}>
          <YearItem
            lang={lang}
            mode={mode}
            active={activeYear === "全部"}
            onClick={() => setActiveYear("全部")}
            label={I18N.yearAll[lang]}
          />
          {years.map((y) => (
            <YearItem
              lang={lang}
              key={y}
              mode={mode}
              active={activeYear === y}
              onClick={() => setActiveYear(y)}
              label={String(y)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href={"/"}
          >
            {I18N.breadcrumbHome[lang]}
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href={"/最新資訊"}
          >
            {I18N.breadcrumbInfo[lang]}
          </Link>
          <Typography color="text.primary">
            {I18N.breadcrumbLatest[lang]}
          </Typography>
        </Breadcrumbs>
      </Container>

      {/* Filter chips row */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          useFlexGap
          sx={{ mt: 1 }}
        >
          {filterKeys.map((key) => (
            <Chip
              key={key}
              label={key === "全部" ? I18N.filterAll[lang] : key}
              onClick={() => setActiveCat(key)}
              variant={activeCat === key ? "filled" : "outlined"}
              color="primary"
              size={isDesktopLayout ? "medium" : "small"}
              sx={{
                borderRadius: 999,
                bgcolor:
                  activeCat === key
                    ? alpha(getPrimaryColor(mode), 1.15)
                    : "transparent",
              }}
            />
          ))}
        </Stack>
      </Container>

      {heroRightImage && (
        <FlagImage sx={{ backgroundImage: `url(${heroRightImage})` }} />
      )}

      {/* BODY */}
      <Container
        maxWidth={false}
        sx={{
          mt: isDesktopLayout ? 2 : 1,
          mb: isDesktopLayout ? 10 : 6,
          maxWidth: 1200,
          mx: "auto",
          px: isDesktopLayout ? 4 : 2,
        }}
      >
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isDesktopLayout
                ? "minmax(0, 1fr) 180px"
                : "1fr",
              columnGap: isDesktopLayout ? 4 : 0,
              alignItems: "flex-start",
            }}
          >
            {/* Mobile: year on top */}
            <Box
              sx={{
                display: isDesktopLayout ? "none" : "block",
                gridColumn: "1 / -1",
                mb: 2,
              }}
            >
              {YearSidebar}
            </Box>

            {/* Left column: cards */}
            <Box
              sx={{
                gridColumn: isDesktopLayout ? "1 / 2" : "1 / -1",
              }}
            >
              {grouped.length === 0 ? (
                <Typography sx={{ opacity: 0.7, mt: 2 }}>
                  {I18N.noResult[lang]}
                </Typography>
              ) : (
                grouped.map(([mon, items]) => (
                  <Box
                    key={mon}
                    sx={{
                      mb: isDesktopLayout ? 6 : 4,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: getPrimaryColor(mode),
                        fontWeight: 700,
                        mb: 1,
                        letterSpacing: 0.2,
                      }}
                    >
                      {monthTitle(mon, lang)}
                    </Typography>

                    <Divider
                      sx={{
                        mb: isDesktopLayout ? 3 : 2,
                        borderColor: alpha(getPrimaryColor(mode), 0.4),
                        borderBottomWidth: 2,
                        maxWidth: isDesktopLayout ? 450 : "100%",
                      }}
                    />

                    <Stack
                      spacing={isDesktopLayout ? 3.5 : 2.5}
                      sx={{ width: "100%", alignItems: "stretch" }}
                    >
                      {items.map((p) => (
                        <Card
                          key={p.id}
                          elevation={0}
                          sx={{
                            width: "100%",
                            maxWidth: isDesktopLayout ? 450 : "100%",
                            alignSelf: "flex-start",
                            mr: "auto",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            overflow: "hidden",
                            bgcolor: "background.paper",
                          }}
                        >
                          <CardActionArea
                            component={RouterLink}
                            to={p.link}
                            sx={{
                              display: "flex",
                              alignItems: "stretch",
                              width: "100%",
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={p.image}
                              alt={p.title}
                              sx={{
                                width: isDesktopLayout ? 160 : { xs: 96, sm: 128 },
                                height: isDesktopLayout
                                  ? 160
                                  : { xs: 96, sm: 128 },
                                objectFit: "cover",
                                flexShrink: 0,
                                bgcolor: "grey.100",
                              }}
                            />

                            <CardContent
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                p: isDesktopLayout ? 2.5 : 1.5,
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.25}
                                alignItems="center"
                                sx={{ mb: 0.75, flexWrap: "wrap" }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    color: getPrimaryColor(mode),
                                    fontWeight: 700,
                                    fontFamily: fonts[lang],
                                  }}
                                >
                                  {p.displayDate}
                                </Typography>

                                {p.category ? (
                                  <Chip
                                    label={p.category}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      borderRadius: 999,
                                      bgcolor: getPrimaryColor(mode),
                                      fontFamily: fonts[lang],
                                      fontWeight: 700,
                                      alignItems: "center",
                                    }}
                                  />
                                ) : null}
                              </Stack>

                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  fontFamily: fonts[lang],
                                  mb: 0.5,
                                  lineHeight: 1.35,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {p.title}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  fontWeight: 400,
                                  fontFamily: fonts[lang],
                                }}
                              >
                                {p.summary}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                ))
              )}
            </Box>

            {/* Right column: year list */}
            <Box
              sx={{
                display: isDesktopLayout ? "block" : "none",
                gridColumn: "2 / 3",
              }}
            >
              {YearSidebar}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
